import * as Styled from './ConfirmDialog.styles';

import { AlertTriangle, Check, Info, X } from 'lucide-react';
import { FC, MouseEvent, ReactNode } from 'react';

import { DialogVariant } from './ConfirmDialog.styles';
import { PunchupButton } from 'features/common/components/Form/PunchupButton/PunchupButton';
import { useIframeDialogClamp } from 'features/common/hooks/useIframeDialogClamp';
import ReactDOM from 'react-dom';

interface ConfirmDialogProps {
	variant?: DialogVariant;
	title: string;
	description: ReactNode;
	confirmLabel: string;
	cancelLabel: string;
	loading?: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}

const VARIANT_ICONS: Record<DialogVariant, ReactNode> = {
	success: <Check size={20} strokeWidth={2.5} />,
	danger: <X size={20} strokeWidth={2.5} />,
	warning: <AlertTriangle size={20} strokeWidth={2.5} />,
	info: <Info size={20} strokeWidth={2.5} />,
};

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
	variant = 'info',
	title,
	description,
	confirmLabel,
	cancelLabel,
	loading,
	onCancel,
	onConfirm,
}) => {
	const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget && !loading) onCancel();
	};

	// Clamp to the parent's visible band when embedded, so the card fits the mobile fold and its
	// buttons stay reachable (TIC-636). Standalone keeps the CSS sizing (both styles undefined).
	const { overlayStyle, containerStyle } = useIframeDialogClamp();

	return ReactDOM.createPortal(
		<Styled.Overlay onMouseDown={handleBackdropMouseDown} style={overlayStyle}>
			<Styled.Card style={containerStyle}>
				<Styled.IconBadge variant={variant}>{VARIANT_ICONS[variant]}</Styled.IconBadge>
				<Styled.Title>{title}</Styled.Title>
				<Styled.Description>{description}</Styled.Description>
				<Styled.Actions>
					<PunchupButton variant="secondary" text={cancelLabel} onClick={onCancel} disabled={loading} />
					<PunchupButton variant="primary" text={confirmLabel} onClick={onConfirm} disabled={loading} />
				</Styled.Actions>
			</Styled.Card>
		</Styled.Overlay>,
		document.body
	);
};
